import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "wouter";
import { useGetCoach, useUpdateCoach, getGetCoachQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { ArrowLeft, Mail, Phone, Calendar, ShieldCheck, FileText, User, AlertTriangle, Loader2, Eye, EyeOff, DollarSign, Bell, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CommentsSection } from "@/components/CommentsSection";
import { SampleBadge } from "@/components/SampleBadge";
import { CustomFieldsSection, saveCustomFieldValues } from "@/components/CustomFieldsSection";
import { useOrg } from "@/lib/orgContext";
import { useAuth } from "@clerk/react";
import { useToast } from "@/hooks/use-toast";

function CoachPortalAccessCard({ coachId, coachEmail }: { coachId: number; coachEmail: string | null }) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [clerkUserId, setClerkUserId] = useState("");
  const [status, setStatus] = useState<string>("invited");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const coachRes = await fetch(`/api/coaches/${coachId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (coachRes.ok) {
          const c = await coachRes.json();
          if (!cancelled) {
            setEnabled(Boolean(c.portalAccessEnabled));
            setClerkUserId(c.clerkUserId ?? "");
            setStatus(c.portalStatus ?? "invited");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [coachId, getToken]);

  const sendInvite = async () => {
    if (!coachEmail) {
      toast({ title: "Coach has no email on file", variant: "destructive" });
      return;
    }
    setInviting(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/coaches/${coachId}/portal-invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setEnabled(true);
        setStatus("invited");
        toast({
          title: "Portal invite sent",
          description: data?.emailSent === false
            ? `Email could not be delivered to ${coachEmail}. Check email settings.`
            : `Activation link sent to ${coachEmail}.`,
        });
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: "Invite failed", description: err?.message ?? err?.error ?? "Unknown error", variant: "destructive" });
      }
    } finally {
      setInviting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/coaches/${coachId}/portal-access`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          portalAccessEnabled: enabled,
          clerkUserId: clerkUserId.trim() || null,
          portalStatus: status,
        }),
      });
      if (res.ok) {
        toast({ title: "Portal access updated" });
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: "Save failed", description: err?.error ?? "Unknown error", variant: "destructive" });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" /> Coach Portal Access
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Grant this coach a private portal. They will only see contracts and documents you've explicitly marked visible.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="text-sm flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  data-testid="checkbox-portal-enabled"
                />
                Portal enabled
              </label>
              <input
                type="hidden"
                value={clerkUserId}
                data-testid="input-portal-clerk-user-id"
              />
              <div>
                <label className="text-xs text-muted-foreground">Status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="invited">Invited</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                {coachEmail && (
                  <>Invite will be emailed to <span className="kpi-value">{coachEmail}</span></>
                )}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={sendInvite}
                  disabled={inviting || !coachEmail}
                  data-testid="button-portal-send-invite"
                >
                  {inviting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  Send portal invite
                </Button>
                <Button size="sm" onClick={save} disabled={saving} data-testid="button-portal-save">
                  {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  Save portal access
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type PortalPreview = {
  portalAccessEnabled: boolean;
  portalStatus: string | null;
  coach: {
    id: number; firstName: string; lastName: string; sport: string; title: string;
    email: string; phone: string | null; hireDate: string; status: string;
    lastPortalLoginAt: string | null;
  };
  contract: null | {
    id: number; contractType: string; startDate: string | null; endDate: string | null;
    status: string; updatedAt: string;
    annualSalary: string | number | null; signingBonus: string | number | null;
    compensationVisibleToCoach: boolean;
    keyDates: Array<{ label: string; date: string; type?: string; visible: boolean }>;
    bonuses: Array<{ id?: string; label: string; amount?: number; eligibilityDate?: string; status?: string }>;
  };
  documents: Array<{
    id: number; label: string; fileName: string; mimeType: string; sizeBytes: number;
    uploadedAt: string; visibleToCoach: boolean; coachCanDownload: boolean; scanStatus: string;
  }>;
  tasks: Array<{
    id: number; title: string; instructions: string | null; dueDate: string | null;
    status: string; taskType: string; visibleToCoach: boolean; completedAt: string | null;
  }>;
  updates: Array<{ kind: string; label: string; at: string; visibleToCoach?: boolean }>;
  contact: {
    institutionName: string | null; hrContact: string | null;
    sportAdminContact: string | null; supportEmail: string | null;
  };
};

function HiddenTag({ label = "Hidden from coach" }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-medium px-2 py-0.5">
      <EyeOff className="h-3 w-3" /> {label}
    </span>
  );
}

function fmtMoney(v: string | number | null | undefined) {
  if (v == null || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function fmtDate(s: string | null | undefined) {
  if (!s) return "—";
  try { return format(new Date(s), "MMM d, yyyy"); } catch { return s; }
}

function humanizeAuditAction(action: string): string {
  switch (action) {
    case "login": return "Signed in to portal";
    case "document_viewed": return "Viewed a document";
    case "document_downloaded": return "Downloaded a document";
    case "document_acknowledged": return "Acknowledged a document";
    case "file_uploaded": return "Uploaded a file";
    case "disclosure_submitted": return "Submitted a disclosure";
    case "task_completed": return "Completed a task";
    default: return action.replace(/_/g, " ");
  }
}

function CoachPortalPreviewCard({ coachId }: { coachId: number }) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PortalPreview | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`/api/coaches/${coachId}/portal-preview`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        setError("Could not load preview");
        setData(null);
      } else {
        setData(await res.json());
      }
    } catch {
      setError("Could not load preview");
    } finally {
      setLoading(false);
    }
  }, [coachId, getToken]);

  useEffect(() => { void load(); }, [load]);

  return (
    <Card data-testid="card-portal-preview">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="h-5 w-5 text-muted-foreground" /> Preview portal as this coach
          </CardTitle>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="button-portal-preview-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Refresh
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Read-only mirror of <code>/portal</code>. Items hidden by visibility flags are clearly marked so you can spot accidental over- or under-sharing.
        </p>
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <Skeleton className="h-40 w-full" />
        ) : error ? (
          <div className="text-sm text-muted-foreground">{error}</div>
        ) : !data ? null : (
          <div className="space-y-4">
            {!data.portalAccessEnabled && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Portal preview only — this coach cannot sign in until you enable access above.
              </div>
            )}

            {/* 1. Welcome */}
            <section className="rounded-md border bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">1 · Welcome</div>
              <div className="font-semibold">Welcome, {data.coach.firstName}</div>
              <div className="text-sm text-muted-foreground">{data.coach.title} · {data.coach.sport}</div>
            </section>

            {/* 2. Contract overview */}
            <section className="rounded-md border bg-white p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">2 · Your active contract</div>
              {data.contract ? (
                <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
                  <dt className="text-muted-foreground">Type</dt><dd className="font-medium">{data.contract.contractType}</dd>
                  <dt className="text-muted-foreground">Start date</dt><dd>{fmtDate(data.contract.startDate)}</dd>
                  <dt className="text-muted-foreground">End date</dt><dd>{fmtDate(data.contract.endDate)}</dd>
                  <dt className="text-muted-foreground">Status</dt><dd><Badge variant="green">{data.contract.status}</Badge></dd>
                </dl>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No active contract on file — coach would see "No active contract on file."
                </p>
              )}
            </section>

            {/* 3. Key dates */}
            <section className="rounded-md border bg-white p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> 3 · Key dates
              </div>
              {!data.contract ? (
                <p className="text-sm text-muted-foreground italic">No active contract — section is empty for the coach.</p>
              ) : data.contract.keyDates.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No key dates configured. Coach sees "No key dates have been published yet."</p>
              ) : (
                <ul className="divide-y text-sm">
                  {data.contract.keyDates.map((d, i) => (
                    <li key={i} className={`py-1.5 flex items-center justify-between gap-2 ${d.visible ? "" : "opacity-60"}`} data-testid={`portal-preview-keydate-${i}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate">{d.label}</span>
                        {!d.visible && <HiddenTag />}
                      </div>
                      <span className="font-medium shrink-0">{fmtDate(d.date)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* 4. Compensation */}
            <section className="rounded-md border bg-white p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5" /> 4 · Compensation
              </div>
              {!data.contract ? (
                <p className="text-sm text-muted-foreground italic">No active contract — section is empty.</p>
              ) : !data.contract.compensationVisibleToCoach ? (
                <div className="flex items-center gap-2 text-sm">
                  <HiddenTag label="Entire section hidden from coach" />
                  <span className="text-muted-foreground">Coach sees: "Compensation details are not currently shared with the portal."</span>
                </div>
              ) : (
                <div className="text-sm space-y-2">
                  <div className="grid grid-cols-2 gap-y-1.5">
                    <div className="text-muted-foreground">Annual salary</div>
                    <div className="font-medium">{fmtMoney(data.contract.annualSalary)}</div>
                    <div className="text-muted-foreground">Signing bonus</div>
                    <div className="font-medium">{fmtMoney(data.contract.signingBonus)}</div>
                  </div>
                  {data.contract.bonuses.length > 0 && (
                    <div>
                      <div className="font-semibold mt-2 text-sm">Performance bonuses</div>
                      <ul className="divide-y mt-1">
                        {data.contract.bonuses.map((b, i) => (
                          <li key={i} className="py-1.5 flex justify-between">
                            <span>{b.label}{b.eligibilityDate ? ` · ${fmtDate(b.eligibilityDate)}` : ""}</span>
                            <span className="font-medium">{fmtMoney(b.amount ?? null)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* 5. Documents */}
            <section className="rounded-md border bg-white p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" /> 5 · Shared documents
              </div>
              {data.documents.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No documents on this contract. Coach sees "No documents have been shared with you yet."</p>
              ) : (
                <ul className="divide-y text-sm">
                  {data.documents.map((d) => (
                    <li
                      key={d.id}
                      className={`py-2 flex items-start justify-between gap-3 ${d.visibleToCoach ? "" : "opacity-60"}`}
                      data-testid={`portal-preview-doc-${d.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.fileName}</div>
                        <div className="text-xs text-muted-foreground">{d.label} · uploaded {fmtDate(d.uploadedAt)}</div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 shrink-0 justify-end">
                        {!d.visibleToCoach && <HiddenTag />}
                        {d.visibleToCoach && !d.coachCanDownload && <HiddenTag label="View only — download blocked" />}
                        {d.visibleToCoach && d.coachCanDownload && d.scanStatus !== "clean" && (
                          <HiddenTag label={`Download blocked (scan: ${d.scanStatus})`} />
                        )}
                        {d.visibleToCoach && d.coachCanDownload && d.scanStatus === "clean" && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-900 text-[10px] font-medium px-2 py-0.5">
                            <Eye className="h-3 w-3" /> Download
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* 6. Action items */}
            <section className="rounded-md border bg-white p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <Bell className="h-3.5 w-3.5" /> 6 · Action items
              </div>
              {data.tasks.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No tasks. Coach sees "No outstanding tasks."</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.tasks.map((t) => (
                    <li
                      key={t.id}
                      className={`rounded border p-2 flex items-start justify-between gap-2 ${t.visibleToCoach ? "" : "opacity-60"}`}
                      data-testid={`portal-preview-task-${t.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium">{t.title}</div>
                        {t.dueDate && <div className="text-xs text-muted-foreground">Due {fmtDate(t.dueDate)}</div>}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Badge variant={t.status === "completed" ? "muted" : "gold"}>{t.status}</Badge>
                        {!t.visibleToCoach && <HiddenTag />}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* 7. Recent updates */}
            <section className="rounded-md border bg-white p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">7 · Recent activity</div>
              {data.updates.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No recent activity yet.</p>
              ) : (
                <ul className="divide-y text-sm">
                  {data.updates.slice(0, 10).map((u, i) => (
                    <li key={i} className={`py-1.5 flex justify-between gap-2 ${u.visibleToCoach === false ? "opacity-60" : ""}`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="truncate">{u.label || humanizeAuditAction(u.kind)}</span>
                        {u.visibleToCoach === false && <HiddenTag />}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">{new Date(u.at).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Contact */}
            <section className="rounded-md border bg-white p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" /> Contact your athletic department
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 text-sm">
                {data.contact.institutionName && (<><dt className="text-muted-foreground">Institution</dt><dd className="font-medium">{data.contact.institutionName}</dd></>)}
                <dt className="text-muted-foreground">HR contact</dt>
                <dd>{data.contact.hrContact || <span className="italic text-muted-foreground">Not set</span>}</dd>
                <dt className="text-muted-foreground">Sport administrator</dt>
                <dd>{data.contact.sportAdminContact || <span className="italic text-muted-foreground">Not set</span>}</dd>
                <dt className="text-muted-foreground">Support email</dt>
                <dd>{data.contact.supportEmail || <span className="italic text-muted-foreground">Not set</span>}</dd>
              </dl>
            </section>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function statusVariant(status: string | null | undefined): "green" | "gold" | "red" | "muted" {
  if (status === "active") return "green";
  if (status === "pending") return "gold";
  if (status === "expired" || status === "terminated") return "muted";
  return "muted";
}

function contractStatusVariant(status: string | null | undefined): "green" | "gold" | "red" | "muted" {
  if (status === "active") return "green";
  if (status === "pending") return "gold";
  if (status === "expired") return "red";
  return "muted";
}

function insuranceStatusVariant(status: string | null | undefined): "green" | "gold" | "red" | "muted" {
  if (status === "compliant") return "green";
  if (status === "issues") return "gold";
  if (status === "missing") return "red";
  return "muted";
}

export default function CoachDetail() {
  const { id } = useParams<{ id: string }>();
  const coachId = parseInt(id, 10);
  const { org } = useOrg();
  const { getToken } = useAuth();
  const { toast } = useToast();
  const isAdmin = org?.role === "admin";
  const [cfValues, setCfValues] = useState<Record<string, unknown>>({});
  const [cfSaving, setCfSaving] = useState(false);

  const validTabs = ["insurance", "custom_fields", "comments"];
  const getTabFromHash = () => {
    const hash = window.location.hash.replace(/^#/, "");
    return validTabs.includes(hash) ? hash : "insurance";
  };
  const [activeTab, setActiveTab] = useState<string>(getTabFromHash);
  useEffect(() => {
    const onHashChange = () => setActiveTab(getTabFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const qc = useQueryClient();
  const handleCfSave = useCallback(async () => {
    if (!isAdmin) return;
    setCfSaving(true);
    try {
      const token = await getToken();
      if (!token) return;
      const result = await saveCustomFieldValues("coach", coachId, cfValues, token);
      if (result.ok) {
        toast({ title: "Custom fields saved" });
      } else {
        toast({ title: "Failed to save custom fields", variant: "destructive" });
      }
    } finally {
      setCfSaving(false);
    }
  }, [coachId, cfValues, getToken, isAdmin, toast]);
  const { data: coach, isLoading } = useGetCoach(coachId, {
    query: { enabled: !!coachId, queryKey: getGetCoachQueryKey(coachId) }
  });
  const updateCoachMut = useUpdateCoach({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetCoachQueryKey(coachId) });
        toast({ title: "Updated", description: "Program gender saved." });
      },
      onError: () => toast({ title: "Save failed", variant: "destructive" }),
    },
  });
  const programGenderLabel: Record<string, string> = { mens: "Men's", womens: "Women's", coed: "Co-ed" };

  if (isLoading || !coach) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-[300px]" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start sm:items-center gap-3 sm:gap-4">
        <Button variant="outline" size="icon" asChild className="shrink-0">
          <Link href="/coaches"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div className="page-header min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1>{coach.firstName} {coach.lastName}</h1>
            <Badge variant={statusVariant(coach.status)}>{coach.status}</Badge>
            {coach.isSample && <SampleBadge />}
          </div>
          <p>
            {coach.title} · {coach.sport}
            {coach.programGender && (
              <Badge variant="muted" className="ml-2">{programGenderLabel[coach.programGender] ?? coach.programGender}</Badge>
            )}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Program Gender (Title IX equity)</CardTitle>
        </CardHeader>
        <CardContent>
          {isAdmin ? (
            <Select
              value={coach.programGender ?? "_unset"}
              onValueChange={(v) =>
                updateCoachMut.mutate({
                  id: coachId,
                  data: { programGender: v === "_unset" ? null : (v as "mens" | "womens" | "coed") },
                })
              }
              disabled={updateCoachMut.isPending}
            >
              <SelectTrigger className="w-56"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_unset">Unassigned</SelectItem>
                <SelectItem value="mens">Men's</SelectItem>
                <SelectItem value="womens">Women's</SelectItem>
                <SelectItem value="coed">Co-ed</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm">{coach.programGender ? programGenderLabel[coach.programGender] : "Unassigned"}</div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">Contract Status</CardTitle>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-field-green-subtle">
              <FileText className="h-4 w-4 text-field-green" />
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Badge variant={contractStatusVariant(coach.contractStatus)}>{coach.contractStatus ?? "none"}</Badge>
            {coach.activeContractId && (
              <Button variant="link" className="px-0 h-auto mt-2 text-xs" asChild>
                <Link href={`/contracts/${coach.activeContractId}`}>View Active Contract →</Link>
              </Button>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              <Button variant="outline" size="sm" className="h-7 text-xs" asChild data-testid="button-coach-new-term-sheet">
                <Link href={`/term-sheets/new?coachId=${coach.id}`}>New Term Sheet</Link>
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs" asChild data-testid="button-coach-new-contract">
                <Link href={`/contracts/new?coachId=${coach.id}`}>New Contract</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">Insurance</CardTitle>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${coach.insuranceStatus === 'compliant' ? 'bg-field-green-subtle' : coach.insuranceStatus === 'missing' ? 'bg-signal-red-subtle' : 'bg-gold-subtle'}`}>
              {coach.insuranceStatus === 'compliant'
                ? <ShieldCheck className="h-4 w-4 text-field-green" />
                : <AlertTriangle className={`h-4 w-4 ${coach.insuranceStatus === 'missing' ? 'text-signal-red' : 'text-gold'}`} />}
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Badge variant={insuranceStatusVariant(coach.insuranceStatus)}>{(coach.insuranceStatus ?? "unknown").replace("_", " ")}</Badge>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">Hired</CardTitle>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-muted">
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-base font-bold kpi-value tracking-tight">{format(new Date(coach.hireDate), 'MMM d, yyyy')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">Policies</CardTitle>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-navy-subtle">
              <ShieldCheck className="h-4 w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold kpi-value tracking-tight">{coach.insurancePolicies?.length ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1 leading-tight">on file</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><User className="h-5 w-5 text-muted-foreground"/> Contact Info</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="truncate">{coach.email}</span>
            </div>
            {coach.phone && (
              <div className="flex items-center gap-3 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="kpi-value">{coach.phone}</span>
              </div>
            )}
            <div className="flex items-center gap-3 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>Hired <span className="kpi-value">{format(new Date(coach.hireDate), 'MMM d, yyyy')}</span></span>
            </div>
          </div>
        </CardContent>
      </Card>

      {isAdmin && <CoachPortalAccessCard coachId={coachId} coachEmail={coach.email ?? null} />}
      {isAdmin && <CoachPortalPreviewCard coachId={coachId} />}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="insurance">Insurance Policies</TabsTrigger>
          <TabsTrigger value="custom_fields">Custom Fields</TabsTrigger>
          <TabsTrigger value="comments">Comments</TabsTrigger>
        </TabsList>

        <TabsContent value="custom_fields" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-muted-foreground" /> Custom Fields</CardTitle>
              {isAdmin && (
                <Button size="sm" onClick={handleCfSave} disabled={cfSaving}>
                  {cfSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  {cfSaving ? "Saving…" : "Save Changes"}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <CustomFieldsSection
                entityType="coach"
                entityId={coachId}
                readOnly={!isAdmin}
                values={isAdmin ? cfValues : undefined}
                onChange={isAdmin ? (key, value) => setCfValues((prev) => ({ ...prev, [key]: value })) : undefined}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="insurance" className="mt-6 space-y-4">
           {coach.insurancePolicies && coach.insurancePolicies.length > 0 ? (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {coach.insurancePolicies.map(policy => (
                   <Card key={policy.id}>
                     <CardContent className="p-4 flex flex-col gap-2">
                        <div className="flex justify-between items-start">
                           <div className="font-medium capitalize">{policy.policyType.replace('_', ' ')}</div>
                           <Badge variant={policy.status === 'active' ? 'green' : 'muted'}>{policy.status.replace('_', ' ')}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground kpi-value">{policy.carrier} • {policy.policyNumber}</div>
                        <div className="text-xs mt-2">Exp: <span className="kpi-value">{format(new Date(policy.endDate), 'MMM d, yyyy')}</span></div>
                     </CardContent>
                   </Card>
                ))}
             </div>
           ) : (
             <Card>
               <CardContent className="p-6 text-center text-muted-foreground">
                 No insurance policies found.
               </CardContent>
             </Card>
           )}
        </TabsContent>
        
        <TabsContent value="comments" className="mt-6">
          <CommentsSection entityType="coach" entityId={coachId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
